/**
 * Glassmorphic straddling tab bar for the competition / league hero (Claude Design
 * handoff). Active tab is an accent pill; labels auto-shrink to fit so five tabs
 * stay on one row on narrow phones.
 */

import React, { useMemo } from 'react'
import { View, Text, Pressable, Platform } from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { getCompetitionTabBarStyles } from './CompetitionTabBar.styles'

export interface CompetitionTabDef<K extends string = string> {
  key: K
  label: string
}

interface CompetitionTabBarProps<K extends string> {
  tabs: readonly CompetitionTabDef<K>[]
  activeKey: K
  onChange: (key: K) => void
  /** Accent fill for the active pill. Defaults to theme.primary. */
  accent?: string
  testID?: string
}

export function CompetitionTabBar<K extends string>({
  tabs,
  activeKey,
  onChange,
  accent,
  testID,
}: CompetitionTabBarProps<K>): React.ReactElement {
  const theme = useTheme()
  const styles = useMemo(
    () => getCompetitionTabBarStyles(theme, accent ?? theme.primary),
    [theme, accent],
  )

  return (
    <View style={styles.bar} accessibilityRole="tablist" testID={testID}>
      {tabs.map((tab) => {
        const active = tab.key === activeKey
        return (
          <Pressable
            key={tab.key}
            onPress={() => onChange(tab.key)}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            accessibilityLabel={tab.label}
            style={({ pressed }) => [
              styles.tab,
              active && styles.tabActive,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && !active && styles.tabPressed,
            ]}
          >
            <Text
              style={[styles.label, active && styles.labelActive]}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.8}
            >
              {tab.label}
            </Text>
          </Pressable>
        )
      })}
    </View>
  )
}
